import type { ChecksState, Check, CheckPayload } from '../types'
import { isResponseRetry } from '@app/utils/predicates'

import { defineStore } from 'pinia'

import { SfLog } from '@app/adapters/logger'
import SFetch from '@app/utils/sfetch'
import {
  CheckStatus,
  checkStatus,
  progressStatus,
} from '@app/application/sf-assistant-checks/constants'
import { checkIsDismissedOrCompleted } from '@app/application/sf-assistant-checks/utils'
import { toggleCheck as toggleCheckApi } from '../api'

export const useChecksStore = defineStore('checksStore', {
  state: (): ChecksState => ({
    accountId: undefined,
    reconciliationId: undefined,
    checks: [],
    timestamp: undefined,
    loadingStatus: progressStatus.NONE,
    setupStatus: progressStatus.NONE,
    error: false,
  }),
  actions: {
    startLoading(): void {
      this.loadingStatus = progressStatus.STARTED
    },
    stopLoading(): void {
      this.loadingStatus = progressStatus.DONE
    },
    refreshChecks(): boolean {
      return true
    },
    async receiveChecks(): Promise<void> {
      if (this.loadingStatus === progressStatus.STARTED) {
        return
      }

      if (!this.accountId && !this.reconciliationId) {
        return
      }

      this.startLoading()

      const v2 = window.Silverfin.currentFirmFeatures.supports_v2_checks
      const route = v2
        ? 'checks_ledger_assistant_checks_v2_index_path'
        : 'checks_ledger_assistant_checks_path'

      const options = this.accountId
        ? { account_id: this.accountId }
        : { reconciliation_id: this.reconciliationId }
      const path = window.Silverfin.Routes[route](
        window.Silverfin.currentFirmId,
        window.Silverfin.currentCompanyId,
        window.Silverfin.currentLedgerId(),
        options
      )

      try {
        const checksPayload = await SFetch.json<CheckPayload>(path, {
          method: 'GET',
        })

        let trigger = false

        if (checksPayload) {
          if (this.accountId) {
            this.checks = checksPayload['accounts'][this.accountId] || []
          }
          if (this.reconciliationId) {
            this.checks =
              checksPayload['reconciliations'][this.reconciliationId] || []
          }
          this.checks = this.checks.map((check) => ({
            ...check,
            read: checksPayload.readStatus.includes(check.id),
          }))

          this.timestamp = checksPayload['timestamp']
          trigger = checksPayload['trigger']
          this.stopLoading()
        } else {
          this.checks = []
          this.timestamp = undefined
          trigger = true
          this.stopLoading()
        }

        if (trigger) {
          await this.triggerCalculation()
        }
      } catch (error) {
        SfLog.warn('Error fetching checks', {
          extra: {
            error,
          },
        })

        this.checks = []
        this.timestamp = undefined
        this.stopLoading()
      }
    },
    async triggerCalculation(): Promise<void> {
      const v2 = window.Silverfin.currentFirmFeatures.supports_v2_checks
      const route = v2
        ? 'trigger_ledger_assistant_checks_v2_index_path'
        : 'trigger_ledger_assistant_checks_path'
      if (this.loadingStatus !== progressStatus.STARTED) {
        const url = window.Silverfin.Routes[route](
          window.Silverfin.currentFirmId,
          window.Silverfin.currentCompanyId,
          window.Silverfin.currentLedgerId()
        )

        try {
          await SFetch.withRetry(SFetch.json, {
            maxRetries: 5,
            shouldRetry: isResponseRetry,
          })(url, { method: 'POST', body: {} })
        } catch (error) {
          if (error === 'SFetch::WithRetry::Max retries exceeded') {
            SfLog.info(
              'Max retries exceeded for Silverfin Assistant checks calculation',
              {
                extra: {
                  companyId: window.Silverfin.currentCompanyId,
                  firmId: window.Silverfin.currentFirmId,
                },
              }
            )
          } else {
            SfLog.warn('Error triggering calculation', {
              extra: {
                error,
              },
            })
          }
        }
      }
    },
    async resetChecks(contextType: string, contextId: number): Promise<void> {
      this.accountId = undefined
      this.reconciliationId = undefined
      this.checks = []
      if (contextType === 'account') {
        this.accountId = contextId
      } else {
        this.reconciliationId = contextId
      }
      this.stopLoading()
      await this.receiveChecks()
    },
    async toggleCheckDismissal(check: Check): Promise<void> {
      this.error = false
      const v2 = window.Silverfin.currentFirmFeatures.supports_v2_checks
      const route: keyof typeof window.Silverfin.Routes = `${
        checkIsDismissedOrCompleted(check) ? 'undismiss' : 'dismiss'
      }_ledger_assistant_checks${v2 ? '_v2_index_' : '_'}path`

      const path = window.Silverfin.Routes[route](
        window.Silverfin.currentFirmId,
        window.Silverfin.currentCompanyId,
        window.Silverfin.currentLedgerId(),
        check.id,
        {
          account_id: this.accountId || 0,
          reconciliation_id: this.reconciliationId || 0,
        }
      )

      try {
        await SFetch.json(path, { method: 'POST' })
        const newCheck =
          this.loadingStatus === progressStatus.DONE &&
          this.checks.find((c) => c.id === check.id)

        if (newCheck) {
          newCheck.status = checkIsDismissedOrCompleted(check)
            ? checkStatus.PENDING
            : checkStatus.DISMISSED
        }
      } catch (e) {
        this.error = true
      }
    },
    async toggleCheck(checkId: string): Promise<void> {
      const rowToChange = this.checks.findIndex((check) => check.id === checkId)
      const initialCheckReadStatus = this.checks[rowToChange].read
      this.checks[rowToChange].read = !initialCheckReadStatus

      try {
        await toggleCheckApi({
          type: initialCheckReadStatus ? 'unread' : 'read',
          checkId,
        })
      } catch (ex) {
        this.checks[rowToChange].read = initialCheckReadStatus
      }
    },
  },
  getters: {
    hasChecks(state) {
      return state.checks.length > 0
    },
    visibleChecks(state) {
      return state.checks.filter(
        (check) =>
          ![
            checkStatus.DISMISSED as CheckStatus,
            checkStatus.COMPLETED as CheckStatus,
          ].includes(check.status)
      )
    },
    hiddenChecks(state) {
      return state.checks.filter((check) =>
        [
          checkStatus.DISMISSED as CheckStatus,
          checkStatus.COMPLETED as CheckStatus,
        ].includes(check.status)
      )
    },
  },
})
